import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Calendar, MapPin } from "lucide-react";
import SectionTitle from "../components/SectionTitle";
import InfoBanner from "../components/InfoBanner";
import Seo from "../components/Seo";
import { useData } from "../context/DataContext";
import { resolveImageUrl } from "../admin/ImageUpload";

const Home = () => {
  const { settings, courses, teachers, infos, homeDances, loaded } = useData();
  if (!loaded) {
    return <div className="min-h-[60vh] flex items-center justify-center text-stone-500">Chargement...</div>;
  }
  const SITE = settings || {};
  const heroImage = resolveImageUrl(SITE.heroImage) || "https://images.unsplash.com/photo-1518834107812-67b0b7c58434?w=1920&q=85";
  const dancesCards = (homeDances && homeDances.length > 0) ? homeDances : courses.slice(0, 6);

  return (
    <>
      <Seo
        title={`${SITE.name || "L'Élan Danse"} — École de danse au Perray-en-Yvelines`}
        description={`${SITE.name || "L'Élan Danse"}, école de danse au Perray-en-Yvelines (78). Cours de danse classique, modern jazz, contemporain et éveil pour enfants, ados et adultes.`}
      />

      <section className="relative w-full h-[88vh] min-h-[600px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover"
          style={{
            backgroundImage: `url(${heroImage})`,
            backgroundPosition: `center ${SITE.heroImagePosition !== undefined && SITE.heroImagePosition !== "" ? `${SITE.heroImagePosition}%` : "50%"}`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-stone-900/45 via-stone-900/30 to-stone-900/55" />

        <div className="relative z-10 flex flex-col items-center text-center px-6">
          <p className="text-white/90 font-handwriting text-2xl md:text-3xl tracking-wide drop-shadow-lg">
            {SITE.heroTagline || SITE.tagline || "École de danse au cœur du Perray-en-Yvelines"}
          </p>
          <h1 className="mt-3 text-white font-script text-6xl md:text-8xl drop-shadow-2xl leading-none">
            {SITE.heroTitle || SITE.name || "L'Élan Danse"}
          </h1>
          <p className="mt-6 text-white font-serif-display text-sm md:text-base tracking-[0.35em] uppercase drop-shadow-lg">
            {SITE.heroSubline || `Saison ${SITE.season || ""} · ${SITE.city || "Le Perray-en-Yvelines"}`}
          </p>
          <Link to="/inscription" className="inline-flex items-center gap-2 mt-8 bg-white/95 text-deep-rose px-8 py-3 rounded-full font-serif-display tracking-widest uppercase text-sm hover:bg-white transition-colors shadow-lg">
            {SITE.heroCtaLabel || "S'inscrire"} <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <section className="bg-coral text-white py-10 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif-display text-3xl md:text-4xl tracking-wide">
            Ouverture des inscriptions pour la saison {SITE.nextSeason || SITE.season}
          </h2>
          <p className="font-serif-display text-2xl md:text-3xl mt-2 tracking-wider">{SITE.inscriptionDate}</p>
          <Link to="/inscription" className="inline-flex items-center gap-2 mt-6 bg-white text-coral px-8 py-3 rounded-full font-serif-display tracking-widest uppercase text-sm hover:bg-blush-light transition-colors">
            S&apos;inscrire <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <section className="py-20 px-6">
        <SectionTitle kicker="à ne pas manquer" title="Informations importantes" />
        <div className="space-y-5 max-w-5xl mx-auto">
          {infos.map((info, i) => (
            <InfoBanner key={info.id || i} color={info.color || "bg-blush-light"} cta={info.cta} to={info.link || "/"}>
              {info.text}
            </InfoBanner>
          ))}
        </div>
      </section>

      <section className="py-20 px-6 bg-blush-light/60">
        <SectionTitle kicker="L'équipe pédagogique" title="Les Professeurs" subtitle="Une équipe passionnée qui transmet son amour de la danse avec exigence et bienveillance." />

        <div className="max-w-6xl mx-auto space-y-24 mt-14">
          {teachers.map((t, idx) => {
            const isOdd = idx % 2 === 1;
            const colors = ["bg-mauve/40", "bg-cream-yellow", "bg-peach", "bg-sky-soft"];
            return (
              <div key={t.id || t.slug} className={`${colors[idx % colors.length]} rounded-3xl p-8 md:p-14 grid md:grid-cols-2 gap-10 items-center ${isOdd ? "md:[&>div:first-child]:order-2" : ""}`}>
                <div className="flex justify-center">
                  <div className={`polaroid ${isOdd ? "right" : ""} max-w-[300px]`}>
                    <img src={resolveImageUrl(t.photo) || t.photo} alt={t.name} className="w-full h-[340px] object-cover" />
                    <div className="font-handwriting text-2xl text-stone-700 text-center mt-4">{t.discipline}</div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <span className={`w-4 h-4 rounded-full ${t.color || "bg-stone-400"}`} />
                    <h3 className="font-script text-5xl md:text-6xl text-deep-rose">{t.name}</h3>
                  </div>
                  <p className="font-serif-display text-stone-700 leading-relaxed text-lg mt-4">{t.bio}</p>
                  {t.quote && (
                    <p className="font-serif-display italic text-deep-rose mt-5 text-lg leading-relaxed border-l-2 border-deep-rose pl-4">{t.quote}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="py-20 px-6">
        <SectionTitle
          kicker={SITE.homeDancesKicker || "Nos cours"}
          title={SITE.homeDancesTitle || "Une danse pour chacun"}
          subtitle={SITE.homeDancesSubtitle || "Des plus petits aux adultes, nous proposons un éventail de disciplines pour tous les âges et tous les niveaux."}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {dancesCards.map((c) => {
            const linkTo = c.link || "/cours-tenues";
            const ageLabel = c.ageRange || c.age || "";
            const desc = c.description || c.desc || "";
            const key = c.id || c.slug || c.name;
            return (
              <Link to={linkTo} key={key} className="group block bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-300">
                <div className="h-56 overflow-hidden">
                  <img src={resolveImageUrl(c.image) || c.image} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  {ageLabel && (
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-deep-rose font-serif-display">
                      <Sparkles size={14} /> {ageLabel}
                    </div>
                  )}
                  <h3 className="font-serif-display text-2xl text-stone-800 mt-2">{c.name}</h3>
                  <p className="text-stone-600 mt-2 text-sm leading-relaxed">{desc}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="py-20 px-6 bg-blush-light/40">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="font-handwriting text-2xl text-deep-rose">{SITE.schoolBlockKicker || "L'école"}</span>
            <h2 className="font-serif-display text-4xl text-stone-800 mt-2">{SITE.schoolBlockTitle || "Au cœur du Perray-en-Yvelines"}</h2>
            <div className="section-divider !mx-0 !w-24 my-5" />
            <p className="text-stone-700 leading-relaxed font-serif-display text-lg whitespace-pre-line">
              {SITE.schoolBlockText || `Installée dans un studio lumineux et chaleureux, ${SITE.name || "L'Élan Danse"} accueille petits et grands dans un esprit familial, exigeant et bienveillant. Notre objectif : faire de chaque cours un moment privilégié, où les danseurs s'expriment librement et grandissent au rythme de leurs passions.`}
            </p>
            <ul className="mt-6 space-y-3 text-stone-700 font-serif-display">
              <li className="flex items-center gap-3"><MapPin size={18} className="text-deep-rose" /> {SITE.address}</li>
              <li className="flex items-center gap-3"><Calendar size={18} className="text-deep-rose" /> {SITE.schoolBlockScheduleText || `Saison ${SITE.season} — cours du lundi au samedi`}</li>
            </ul>
          </div>
          <div className="polaroid right max-w-md mx-auto">
            <img src={resolveImageUrl(SITE.schoolBlockImage) || SITE.schoolBlockImage || "https://images.pexels.com/photos/358010/pexels-photo-358010.jpeg?w=900&q=80"} alt={SITE.schoolBlockImageCaption || "Studio L'Élan Danse"} className="w-full h-[380px] object-cover" />
            <div className="font-handwriting text-2xl text-stone-700 text-center mt-3">{SITE.schoolBlockImageCaption || "Notre studio"}</div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
